import { useQuery } from "@tanstack/react-query";

import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

import type { ActiveCustomerProfile } from "../schemas/active-customer-profile-schema.ts";
import { activeCustomerProfileSchema } from "../schemas/active-customer-profile-schema.ts";

export const ACTIVE_CUSTOMER_PROFILE_QUERY_KEY = [
	"active-customer-profile",
] as const;

/** Role-suffixed cache key so vendor and customer reads never share a cache
 * entry. Exported for the SET mutation to prime the matching variant. */
export const buildActiveCustomerProfileQueryKey = (isVendor: boolean) =>
	[
		...ACTIVE_CUSTOMER_PROFILE_QUERY_KEY,
		isVendor ? "vendor" : "customer",
	] as const;

const getActiveCustomerProfile = async (
	url: string,
): Promise<ActiveCustomerProfile> => {
	const response = await getAndValidate(url, activeCustomerProfileSchema);
	if (!response.success) {
		throw new Error(
			response.error || "Failed to fetch active customer profile",
		);
	}
	return response.data;
};

/**
 * Reads the external user's persisted active customer profile.
 *
 * Vendor vs. customer routing: the BFF rejects calls to the customer endpoint
 * when the user's role for the active customer header is `Vendor`, so the
 * URL must match the role we're currently sending headers for. The query
 * re-keys on `isVendor`, which itself re-keys via `useGetCurrentUserInfo`
 * whenever the selected customer changes — so on swap the cache miss forces
 * a fresh fetch under the right URL.
 *
 * On boot the role is briefly unknown (`useIsVendor` defaults to vendor while
 * loading); we don't gate on `isVendorLoading` because the BFF tolerates the
 * customer URL when no customer header is attached (the very first request
 * after login). Once `selectedCustomer` is set and role resolves, the query
 * re-fires under the correct URL.
 *
 * `retryOnMount: false` is set per `b2c-auth-inprogress-loop` to prevent a
 * remount/refetch loop on errors.
 */
export const useGetActiveCustomerProfile = () => {
	const { isVendor } = useIsVendor();
	const url = pickEndpoint(
		{
			internal: apiRoutes.ACTIVECUSTOMERPROFILE,
			external: apiRoutes.ACTIVECUSTOMERPROFILE,
			vendor: apiRoutes.VENDORACTIVECUSTOMERPROFILE,
		},
		{ isInternal: getIsInternal(), isVendor },
	);

	const query = useQuery({
		queryKey: buildActiveCustomerProfileQueryKey(isVendor),
		queryFn: () => getActiveCustomerProfile(url),
		retry: false,
		refetchOnMount: false,
		retryOnMount: false,
		enabled: !getIsInternal(),
	});

	return {
		data: query.data,
		isLoading: query.isLoading,
		error: query.error,
	};
};
